import { get, has, isArray, isEmpty, range, set } from "lodash";
import { logError } from "utils/utils";
import { applyFunction } from "./action";
import { buildPath } from "./common";
import {
  I2dFigure,
  I2dStructure,
  I3dFigure,
  I3dStructure,
  I3dSurface,
  IAction,
  IAnchors,
  IBarChart,
  IBase,
  IExplorer,
  IGetValue,
  IGroup,
  IInput,
  ILayout,
  IList,
  IModel,
  IName,
  IPath,
  IPieChart,
  IResult,
  ITab,
  ITabs,
  ITemplate,
  ITimeChart,
  IValue,
  IWaterfallChart,
  IWaterfallFigure,
  IWaterfallStructure,
} from "./types";

let _uuid = 1;
/**
 * Get a number that will be unique for this js instance.
 * @returns
 */
export const getUniqueId = () => _uuid++;

/**
 * Populate the supplied template inputs and output with a unique id for any item without a name.
 * @param template
 */
export const populateNames = (template: ITemplate): void => {
  let id = 1;
  const populate = (item: IBase) => {
    item.name = isEmpty(item.name) ? `Auto_UUID_${id++}` : item.name;
    (item as ILayout<IBase>).items?.forEach((i) => populate(i));
  };
  template.inputs.forEach((i) => populate(i));
  template.outputs.forEach((i) => populate(i));
};

/**
 * Builds a default data model for the supplied template.
 * @param template
 * @returns
 */
export const buildModel = (template: ITemplate): IModel => {
  const model: IModel = {};
  const calculate: Array<{
    action: IAction;
    path: IPath | IName;
    getValue: IGetValue;
  }> = [];
  const getValue = ((p, m) => get(m, p)) as IGetValue;
  const build = (v: IBase, l?: { name: IName; index: number }): void => {
    if (has(model, v.name)) {
      throw new Error(`The input name "${v.name}" is already defined in ${template.label}.`);
    }
    const gvw = l ? (((p, m) => getValue(buildPath(l.name, l.index, p), m)) as IGetValue) : getValue;
    switch (v.type) {
      case "group":
        (v as IGroup).items.forEach((i) => build(i, l));
        break;
      case "anchors":
        (v as IAnchors).items.forEach((i) => build(i, l));
        break;
      case "tabs":
        (v as ITabs).items.forEach((i) => build(i, l));
        break;
      case "tab":
        (v as ITab).items.forEach((i) => build(i, l));
        break;
      case "explorer":
        (v as IExplorer).items.forEach((i) => build(i, l));
        break;
      case "list":
        if (l) {
          throw new Error(`List input types can't be nested.`);
        }
        const list = v as IList;
        set(model, list.name, []);
        range(0, list.min).forEach((n) => {
          set(model, buildPath(list.name, n, "id"), getUniqueId());
          list.items.forEach((i) => build(i, { name: list.name, index: n }));
        });
        break;
      case "number":
      case "text":
      case "select":
      case "checkbox":
      case "file":
      case "range":
      case "location":
      case "location_matrix":
      case "radio":
      case "label":
      case "calculate":
        const input = v as IInput;
        const path = l ? buildPath(l.name, l.index, v.name) : v.name;
        if (input.default && input.value === undefined) {
          calculate.push({ action: input.default, path, getValue: gvw });
        } else if (input.value === undefined && !["select", "label"].includes(input.type)) {
          logError(
            new Error(`Either value or default must be specified for all input elements (except select): ${v.name}`)
          );
        } else {
          set(model, path, (v as IInput).value);
        }
        break;
      // these types don't need a model entry
      case "image":
      default:
        break;
    }
  };
  template.inputs?.forEach((i) => build(i));
  const sorted = calculate.sort((a, b) =>
    a.action.sources.find((s) => s.includes(b.path) || b.path.includes(s)) ? 1 : -1
  );
  sorted.forEach((t) => {
    const v = applyFunction(model, t.action, t.getValue) as IValue;
    set(model, t.path, v);
  });
  return model;
};

/**
 * Builds a default result model for the supplied template.
 * @param template
 * @returns
 */
export const buildResult = (template: ITemplate): IResult => {
  const random = (max: number, dev: number = 1): number => Math.floor(Math.random() * max * dev);
  const deviate = (): number => Math.random();
  const result: IResult = {};
  const build = (i: IBase): void => {
    if (has(result, [i.name])) {
      throw new Error(`The result name "${i.name}" can only be defined once.`);
    }
    let n = deviate();
    switch (i.type) {
      case "group":
        (i as IGroup).items.forEach((i2) => build(i2));
        break;
      case "anchors":
        (i as IAnchors).items.forEach((i2) => build(i2));
        break;
      case "tabs":
        (i as ITabs).items.forEach((i2) => build(i2));
        break;
      case "tab":
        (i as ITab).items.forEach((i2) => build(i2));
        break;
      case "explorer":
        (i as IExplorer).items.forEach((i2) => build(i2));
        break;
      case "label":
        set(result, i.name, Math.round((random(20000, n) - 10000) * n * 100) / 100);
        break;
      case "bar_chart":
        if ((i as IBarChart).figures.length === 1) {
          const horizontal = (i as IBarChart).horizontal;
          (i as IBarChart).figures.forEach((f) => {
            n = deviate();
            if ((f as I2dStructure).type === "structure") {
              const data = f as I2dStructure;
              const values = range(0, 3)
                .map((v) => v + random(100, n) + Math.floor(v * n))
                .sort((a, b) => (random(1) ? a - b : 0));
              set(
                result,
                data.name,
                ["First", "Second", "Third"].map((v, y) => ({
                  [horizontal ? data.y_value : data.x_value]: v,
                  [horizontal ? data.x_value : data.y_value]: values[y],
                }))
              );
            } else {
              const data = f as I2dFigure;
              set(result, horizontal ? data.y_data.name : data.x_data.name, ["First", "Second", "Third"]);
              set(
                result,
                horizontal ? data.x_data.name : data.y_data.name,
                range(0, 3)
                  .map((v) => v + random(100, n) + Math.floor(v * n))
                  .sort((a, b) => (random(1) ? a - b : 0))
              );
            }
          });
        } else {
          const horizontal = (i as IBarChart).horizontal;
          (i as IBarChart).figures.forEach((f) => {
            n = deviate();
            if ((f as I2dStructure).type === "structure") {
              const data = f as I2dStructure;
              set(
                result,
                data.name,
                range(0, 24)
                  .map((v) => v + random(100, n) + Math.floor(v * n))
                  .sort((a, b) => (random(1) ? a - b : 0))
                  .map((v, y) => ({
                    [horizontal ? data.y_value : data.x_value]: y,
                    [horizontal ? data.x_value : data.y_value]: v,
                  }))
              );
            } else {
              const data = f as I2dFigure;
              set(result, horizontal ? data.y_data.name : data.x_data.name, range(0, 24));
              set(
                result,
                horizontal ? data.x_data.name : data.y_data.name,
                range(0, 24)
                  .map((v) => v + random(100, n) + Math.floor(v * n))
                  .sort((a, b) => (random(1) ? a - b : 0))
              );
            }
          });
        }
        break;
      case "waterfall_chart":
        (i as IWaterfallChart).figures.forEach((f, j) => {
          const concatenate = (i as IWaterfallChart).concatenate;
          n = deviate();
          const m = range(0, 24).map((v) => (v === 0 ? "absolute" : (v + 1) % 6 === 0 ? "total" : "relative"));
          if ((f as IWaterfallStructure).type === "structure") {
            const data = f as IWaterfallStructure;
            set(
              result,
              data.name,
              range(0, 24)
                .map((v) => v + random(100, n) + Math.floor(v * n))
                .map((v, x) => ({
                  ...(data.m_value && { [data.m_value]: m[x] }),
                  [data.x_value]: concatenate ? 24 * j + x : x,
                  [data.y_value]: random(Math.max(0, Math.ceil(x / 3))) ? v : -v,
                }))
            );
          } else {
            const data = f as IWaterfallFigure;
            if (data.m_data) {
              set(result, data.m_data.name, m);
            }
            set(result, data.x_data.name, concatenate ? range(0, 24).map((x) => 24 * j + x) : range(0, 24));
            set(
              result,
              data.y_data.name,
              range(0, 24)
                .map((v) => v + random(100, n) + Math.floor(v * n))
                .map((v, i) => (random(Math.max(0, Math.ceil(i / 3))) ? v : -v))
            );
          }
        });
        break;
      case "time_chart":
        (i as ITimeChart).figures.forEach((f) =>
          f.data.forEach((d) => {
            n = deviate();
            set(
              result,
              d.name,
              range(0, Math.min(Math.ceil((365 * 24 * 60) / d.interval), 1000))
                .map((v) => v + random(100, random(1) ? n : 0.2) + (random(3) ? Math.floor(v * n) : 0))
                .sort((a, b) => (random(1) ? a - b : 0))
            );
          })
        );
        break;
      case "pie_chart":
        (i as IPieChart).figures.forEach((f) => {
          n = deviate();
          if (isArray(f.data)) {
            f.data.forEach((d) => {
              if (!isEmpty(d.name)) {
                set(result, d.name, Math.round(random(10000, n) * n * 100) / 100);
              }
            });
          } else if (f.data.type === "structure") {
            const data = f.data;
            set(
              result,
              f.data.name,
              ["One", "Two", "Three", "Four", "Five"].map((v) => ({
                [data.label]: v,
                [data.value]: Math.round(random(10000, n) * n * 100) / 100,
              }))
            );
          }
        });
        break;
      case "3d_surface":
        (i as I3dSurface).figures.forEach((f) => {
          if ((f as I3dStructure).type === "structure") {
            const data = f as I3dStructure;
            const xvalues = range(0, 100)
              .map((v) => v + random(100, n) + Math.floor(v * n))
              .sort((a, b) => (random(1) ? a - b : 0));
            const yvalues = range(0, 100)
              .map((v) => v + random(100, random(1) ? n : 0.2) + (random(3) ? Math.floor(v * n) : 0))
              .sort((a, b) => (random(1) ? a - b : 0));
            set(
              result,
              data.name,
              range(0, 100)
                .map((v) => v + random(100, random(1) ? n : 0.2))
                .map((z, v) => ({ [data.x_value]: xvalues[v], [data.y_value]: yvalues[v], [data.z_value]: z }))
            );
          } else {
            const data = f as I3dFigure;
            n = deviate();
            set(
              result,
              data.x_data.name,
              range(0, 100)
                .map((v) => v + random(100, n) + Math.floor(v * n))
                .sort((a, b) => (random(1) ? a - b : 0))
            );
            n = deviate();
            set(
              result,
              data.y_data.name,
              range(0, 100)
                .map((v) => v + random(100, random(1) ? n : 0.2) + (random(3) ? Math.floor(v * n) : 0))
                .sort((a, b) => (random(1) ? a - b : 0))
            );
            n = deviate();
            set(
              result,
              data.z_data.name,
              range(0, 100).map((v) => v + random(100, random(1) ? n : 0.2))
            );
          }
        });
        break;
    }
  };
  template.outputs?.forEach((i) => build(i));
  return result;
};
